import { FC, useEffect, useState, type MouseEvent } from 'react';
import { Box, IconButton, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import CopyToClipboardButton from '../../../../atomic/atoms/SCUICopyToClipboardButton/SCUICopyToClipboardButton';
import TimeHelper from '../../../../utils/time';
import { CustomerStoreOrderExpandedPanel } from './CustomerStoreOrderExpandedPanel';
import { downloadStoreReceipt, fetchStoreOrderDetail } from './customerStoreOrders.fetch';
import type { StoreReceiptDisplayMode } from './customerStoreOrders.helpers';
import type { StoreOrderDetailView, StoreOrderListSummary } from './customerStoreOrders.types';
import { CSOP } from './customerStoreOrdersPaper.classes';

interface CustomerStoreOrderRowProps {
    order: StoreOrderListSummary;
    customerId: number;
    orderListType: number;
    isDarkMode: boolean;
    receiptDisplayMode?: StoreReceiptDisplayMode;
}

export const CustomerStoreOrderRow: FC<CustomerStoreOrderRowProps> = ({
    order,
    customerId,
    orderListType,
    isDarkMode,
    receiptDisplayMode = 'download',
}) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detail, setDetail] = useState<StoreOrderDetailView | null>(null);
    const [receiptDownloading, setReceiptDownloading] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        let cancelled = false;
        setDetailLoading(true);
        fetchStoreOrderDetail(customerId, order.orderNumber, orderListType)
            .then((res) => {
                if (!cancelled) {
                    setDetail(res);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDetail(null);
                    Swal.fire(t('general.error'), ' ', 'error');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setDetailLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [open, customerId, order.orderNumber, orderListType, t]);

    const toggleOpen = (event?: MouseEvent) => {
        event?.stopPropagation();
        setOpen((prev) => !prev);
    };

    const handleDownloadReceipt = async () => {
        const orderDate = detail?.orderDate || order.orderDate;
        if (!orderDate) {
            Swal.fire(t('customerStoreOrders.receiptNotAvailable'), ' ', 'info');
            return;
        }
        setReceiptDownloading(true);
        try {
            await downloadStoreReceipt(
                {
                    orderNumber: order.orderNumber,
                    customerId,
                    orderDate,
                },
                receiptDisplayMode
            );
        } catch {
            // downloadStoreReceipt shows Swal
        } finally {
            setReceiptDownloading(false);
        }
    };

    return (
        <>
            <TableRow
                hover
                sx={{ height: '60px !important', cursor: 'pointer' }}
                onClick={() => toggleOpen()}
            >
                <TableCell onClick={toggleOpen}>
                    <IconButton
                        className={CSOP.inputIconButtonExpandRow}
                        aria-label="expand row"
                        size="small"
                        onClick={toggleOpen}
                    >
                        {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                </TableCell>
                <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography component="span" fontWeight={700} fontSize={13}>
                            {order.orderNumber}
                        </Typography>
                        <CopyToClipboardButton textToCopy={order.orderNumber} size="small" />
                    </Box>
                </TableCell>
                <TableCell align="center">{TimeHelper.formatDateHour(order.orderDate)}</TableCell>
                <TableCell align="left">
                    <Tooltip title={order.storeName}>
                        <Typography variant="body2" fontWeight={600} noWrap sx={{ maxWidth: 280 }}>
                            {order.storeName}
                        </Typography>
                    </Tooltip>
                    {order.storeCode && (
                        <Typography variant="caption" color="text.secondary" display="block">
                            {order.storeCode}
                        </Typography>
                    )}
                    {order.statusMessages?.length ? (
                        <Typography
                            component="span"
                            variant="caption"
                            noWrap
                            sx={{ maxWidth: 320, display: 'block', color: order.statusMessages[0].colorCode ?? 'text.secondary' }}
                        >
                            {order.statusMessages
                                .map((m) => {
                                    const key = `customerStoreOrders.${m.text}`;
                                    const translated = t(key, { defaultValue: '' });
                                    return translated || m.text;
                                })
                                .join(' · ')}
                        </Typography>
                    ) : null}
                </TableCell>
                <TableCell align="center">{order.totalPriceStr}</TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ padding: 0, backgroundColor: '#F3F3F3' }} colSpan={5}>
                    {open && (
                        <Box
                            sx={{
                                width: '100%',
                                backgroundColor: isDarkMode ? '#121212' : '#fff',
                            }}
                        >
                            <CustomerStoreOrderExpandedPanel
                                detail={detail}
                                loading={detailLoading}
                                onDownloadReceipt={handleDownloadReceipt}
                                receiptDownloading={receiptDownloading}
                            />
                        </Box>
                    )}
                </TableCell>
            </TableRow>
        </>
    );
};
